import { useEffect } from 'react';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Trans } from '@lingui/react/macro';
import { settingsService } from '@/services/settings';
import type { SettingsResponse } from '@/services/settings';
import MobileNavMenuButton from '@/components/SidebarNavigation/MobileNavMenuButton';
import { useCurrentUser } from '@/services/userHooks';
import { handleLogout } from '@/utils/logout';
import { redirectToLogin } from '@/utils/authRedirect';

export const Route = createFileRoute('/_app/settings')({
  loaderDeps: ({ search }) => ({ ehash: (search as { ehash?: string }).ehash }),
  loader: async ({ deps }) => {
    try {
      return await settingsService.getSettings();
    } catch (error) {
      // /settings/email?ehash=... renders for signed-out visitors: the email
      // tab fetches its own data by ehash and never reads this loader result,
      // so hand it a placeholder instead of failing the whole settings tree.
      // (Cast because the other tabs, which can't be reached with an ehash,
      // keep their non-null loader type.)
      if (deps.ehash) return null as unknown as SettingsResponse;
      console.error('Failed to load settings:', error);
      throw error;
    }
  },
  component: SettingsPage,
  errorComponent: SettingsError,
});

function SettingsPage() {
  const { currentUser } = useCurrentUser();

  return (
    <div className="flex h-full flex-col">
      {/* Page Header */}
      <div className="border-default bg-surface-primary flex-between z-header sticky top-0 border-b px-5 py-3 md:px-10 md:py-4">
        {/* Mobile hamburger menu */}
        <div className="md:hidden">
          <MobileNavMenuButton className="text-copy-secondary" />
        </div>

        <div className="page_title">
          <h1 className="page-title text-[15px] font-semibold">
            <Trans>Settings</Trans>
          </h1>
        </div>
        <div className="top_buttons text-right">
          <button
            onClick={() => {
              if (currentUser?.formKey) {
                handleLogout(currentUser.formKey);
              }
            }}
            className="text-copy-primary text-xs hover:underline"
          >
            <Trans>Sign Out</Trans>
          </button>
        </div>
      </div>

      {/* Child Route Content */}
      <div className="single_page_content flex-1 p-5 md:p-10">
        <Outlet />
      </div>
    </div>
  );
}

function SettingsError({ error }: { error: Error }) {
  // An unauthenticated settings request means there's no session: invite the
  // visitor to sign in rather than showing a dead error screen.
  const isAuthError = (error as { status?: number }).status === 401;
  useEffect(() => {
    if (isAuthError) redirectToLogin();
  }, [isAuthError]);

  if (isAuthError) return null;

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <p className="text-gray-600">
          <Trans>Sorry, there was an error. Please try again.</Trans>
        </p>
      </div>
    </div>
  );
}
